import type { DefaultPrivacyLevel } from '@datadog/browser-rum';

import { appVersion, releaseStage, targetEnvironment } from './environments';

// Force RUM + full Session Replay on the dev deploy so heatmaps have data. Keyed
// off targetEnvironment (always injected by Vite) rather than the optional
// DATADOG_RUM_* vars, so a misconfigured RUM env var can't drop dev to 0%.
const IS_DEVELOPMENT_DEPLOY = targetEnvironment === 'development';
const IS_LOCAL_ENVIRONMENT = targetEnvironment === 'local';

// Gate for dev-only console diagnostics; local does not initialize RUM.
export const APP_IS_DEVELOPMENT = targetEnvironment === 'development';

// applicationId and clientToken are public RUM identifiers (not secrets), safe to
// ship to the browser, but sourced from env vars rather than hardcoded.
export const DATADOG_RUM_ENABLED =
  !IS_LOCAL_ENVIRONMENT && (IS_DEVELOPMENT_DEPLOY || process.env.DATADOG_RUM_ENABLED === 'true');
// eslint-disable-next-line prefer-destructuring
export const DATADOG_RUM_APPLICATION_ID = process.env.DATADOG_RUM_APPLICATION_ID;
// eslint-disable-next-line prefer-destructuring
export const DATADOG_RUM_CLIENT_TOKEN = process.env.DATADOG_RUM_CLIENT_TOKEN;
export const DATADOG_RUM_SITE = process.env.DATADOG_RUM_SITE || 'datadoghq.com';
export const DATADOG_RUM_SERVICE = process.env.DATADOG_RUM_SERVICE || 'workspace_fe';
export const DATADOG_RUM_ENV = process.env.DATADOG_RUM_ENV || releaseStage;
export const DATADOG_RUM_VERSION = process.env.DATADOG_RUM_VERSION || appVersion;
// Fall back to the default when the env var is missing or non-numeric.
function parseSampleRate(value: string | undefined, fallback: number): number {
  const rate = Number(value ?? fallback);
  return Number.isNaN(rate) ? fallback : Math.max(0, Math.min(100, rate));
}

// Dev is forced to 100% so every session is fully recorded; other envs env-driven.
export const DATADOG_RUM_SESSION_SAMPLE_RATE = IS_DEVELOPMENT_DEPLOY
  ? 100
  : parseSampleRate(process.env.DATADOG_RUM_SESSION_SAMPLE_RATE, 100);
export const DATADOG_RUM_SESSION_REPLAY_SAMPLE_RATE = IS_DEVELOPMENT_DEPLOY
  ? 100
  : parseSampleRate(process.env.DATADOG_RUM_SESSION_REPLAY_SAMPLE_RATE, 0);

// Restrict to the privacy levels we support so a typo can't slip through.
const VALID_PRIVACY_LEVELS: readonly DefaultPrivacyLevel[] = ['mask', 'mask-user-input', 'allow'];
const rawPrivacyLevel = process.env.DATADOG_RUM_DEFAULT_PRIVACY_LEVEL || 'mask';
export const DATADOG_RUM_DEFAULT_PRIVACY_LEVEL: DefaultPrivacyLevel = VALID_PRIVACY_LEVELS.includes(
  rawPrivacyLevel as DefaultPrivacyLevel,
)
  ? (rawPrivacyLevel as DefaultPrivacyLevel)
  : 'mask';

// Enables all click tracking; frustration signals are a subset that require it.
export const DATADOG_RUM_TRACK_USER_INTERACTIONS =
  process.env.DATADOG_RUM_TRACK_USER_INTERACTIONS !== 'false';
export const DATADOG_RUM_MANUAL_REPLAY_CONTROL =
  process.env.DATADOG_RUM_MANUAL_REPLAY_CONTROL === 'true';
